// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Button, Stack } from '@mui/material';

// shared components
import InvertedSlider from '@/components/ui/slider/InvertedSlider';

// components
import { formatHour } from '@/utils/formatters';

const GRANULARITY = 4;
const DEFAULT_START = 7;
const DEFAULT_END = 17;

interface WeekDayEntry {
  dayIndex: number;
  enabled: boolean;
  timeFrom: string;
  timeTo: string;
}

interface WeekScheduleEditorProps {
  entries: WeekDayEntry[];
  weekdayKeys: readonly string[];
  onToggle: (dayIndex: number) => void;
  onRangeChange: (dayIndex: number, timeFrom: string, timeTo: string) => void;
}

const hoursToTicks = (hour: number): number => Math.round(hour * GRANULARITY);

const ticksToTime = (ticks: number): string => {
  const clamped = Math.max(0, Math.min(24 * GRANULARITY, ticks));
  const hours = Math.floor(clamped / GRANULARITY)
    .toString()
    .padStart(2, '0');
  const minutes = Math.round((clamped % GRANULARITY) * (60 / GRANULARITY))
    .toString()
    .padStart(2, '0');

  return `${hours}:${minutes}`;
};

const timeToTicks = (time: string, fallbackHour: number): number => {
  const [hoursRaw, minutesRaw] = (time ?? '').split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (
    Number.isFinite(hours) &&
    Number.isFinite(minutes) &&
    hours >= 0 &&
    hours <= 24 &&
    minutes >= 0 &&
    minutes < 60
  ) {
    return Math.round((hours + minutes / 60) * GRANULARITY);
  }

  return hoursToTicks(fallbackHour);
};

const buildMarks = () => {
  const marks = [];
  for (let hour = 0; hour <= 24; hour += 2) {
    marks.push({ value: hour * GRANULARITY, label: String(hour) });
  }
  return marks;
};

const WeekScheduleEditor = ({
  entries,
  weekdayKeys,
  onToggle,
  onRangeChange,
}: WeekScheduleEditorProps) => {
  const { t } = useTranslation('week-schedule-manager');
  const { t: tTime } = useTranslation('time');

  const marks = buildMarks();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
      <Stack spacing={1}>
        {entries.map((entry) => {
          const dayKey = weekdayKeys[entry.dayIndex];
          const isActive = Boolean(entry.enabled);

          const rangeValue = isActive
            ? [
                timeToTicks(entry.timeFrom, DEFAULT_START),
                timeToTicks(entry.timeTo, DEFAULT_END),
              ]
            : [hoursToTicks(DEFAULT_START), hoursToTicks(DEFAULT_END)];

          return (
            <Box
              key={entry.dayIndex}
              sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Box sx={{ minWidth: 90 }}>
                {tTime(`weekdays.${dayKey}.indefinite`)}
              </Box>

              <Box sx={{ flex: 1 }}>
                <InvertedSlider
                  min={0}
                  max={24 * GRANULARITY}
                  step={1}
                  value={rangeValue}
                  onChange={(_, newRange) => {
                    if (!Array.isArray(newRange) || newRange.length !== 2) {
                      return;
                    }

                    const [startTicks, endTicks] = newRange;
                    if (startTicks >= endTicks) {
                      return;
                    }

                    onRangeChange(
                      entry.dayIndex,
                      ticksToTime(startTicks),
                      ticksToTime(endTicks),
                    );
                  }}
                  valueLabelDisplay="on"
                  valueLabelFormat={(value) => formatHour(value / GRANULARITY)}
                  marks={marks}
                  disableSwap
                  disabled={!isActive}
                  sx={{
                    my: 2,
                    opacity: isActive ? 1 : 0.2,
                    transition: 'opacity 0.2s ease',
                    '& .MuiSlider-thumb': {
                      '&::after': { content: '""', display: 'none' },
                    },
                  }}
                />
              </Box>
              <Button
                variant={isActive ? 'contained' : 'outlined'}
                color={isActive ? 'primary' : 'inherit'}
                size="small"
                onClick={() => onToggle(entry.dayIndex)}
              >
                {isActive ? t('edit.excludeDay') : t('edit.includeDay')}
              </Button>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default WeekScheduleEditor;
